import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import ToggleSlide from '@ynap/toggle-slide';
import fetchMatchingHmamContentByMasterCategories from '../SizeChart/fetchMatchingHmamContentByMasterCategories';
import { withOptimizely } from '@ynap/optimizely-utils';

const bem = new BEMHelper('SizeTable');

const SizeTable = ({
    features,
    sizeData,
    title,
    details,
    slideToScroll,
    sizeWrapperClass,
    toggle,
    onToggleUnits,
    onSelectSku,
    textLeft,
    textRight,
    masterCategory,
    skus,
    selectedSku = null,
    setMeasuringGuide = () => {},
    setMeasurements = () => {},
    coreMediaHMAM = {},
    loadingHMAM = false,
    optimizely,
}) => {
    if (!sizeData) return null;
    if (sizeData && sizeData.length <= 1) return null;

    const [hoveredCellColumn, setHoveredCellColumn] = useState(null);

    const { showHMAM: { withOverlay, coreMediaKey } = {} } = features;
    const [headerRow, ...rows] = sizeData;
    const selectedSkuToFind = skus?.find((sku) => sku.size.labelSize === selectedSku);
    const selectedColumnIndex = headerRow.indexOf(selectedSkuToFind?.size?.centralSizeLabel || selectedSku);
    const isSizeSelectable = skus && sizeData[0].length > 2 && onSelectSku;

    const onSelectSkuHandler = (headerData) => {
        onSelectSku(headerData);
        optimizely?.track('select_size_from_chart');
    };

    // note this generates a function so we can bind a measurement label to it in
    // the closure and avoid react rerenders.
    const setContent = (measurementLabel) => () => {
        let measurements = [];

        rows.map((element) => {
            // TODO: remove "replace" after xtracker/pim issues have being fixed
            const sizeValue = element[0].replace('_', ' ');
            const hasMeasurement =
                coreMediaHMAM?.[sizeValue] && fetchMatchingHmamContentByMasterCategories(sizeValue, masterCategory, coreMediaHMAM, withOverlay, coreMediaKey);
            if (hasMeasurement) {
                measurements.push({ ...coreMediaHMAM[sizeValue], measurementLabel: sizeValue });
            }
        });

        setMeasurements(measurements);
        setMeasuringGuide(measurementLabel);
    };

    const handleOnMouseOut = () => {
        setHoveredCellColumn(null);
    };

    const handleOnMouseOver = (index) => {
        if (index === 0) setHoveredCellColumn(null);
        else setHoveredCellColumn(index);
    };

    return (
        <div className={sizeWrapperClass}>
            <h2 className={bem('sizeConversionsTitle')}>{title()}</h2>
            <p className={bem('details')}>{details()}</p>
            <div className={bem('toggleContainerOutside')}>
                {toggle && <ToggleSlide textLeft={textLeft()} textRight={textRight()} onToggle={onToggleUnits} />}
            </div>
            {slideToScroll() && <div className={bem('scrollText')}>{slideToScroll()}</div>}
            <div className={bem('tableWrapper')}>
                <div className={bem('tableContainer')}>
                    <table className={bem('table')}>
                        <thead>
                            <tr>
                                {headerRow.map((header, index) => {
                                    let selectedModifier = index === selectedColumnIndex - 1 && 'columnPreceedingHighlighted';
                                    selectedModifier = selectedModifier || (index === selectedColumnIndex && 'topRowHighlighted');
                                    selectedModifier = isSizeSelectable && selectedModifier;

                                    let columnModifier = null;

                                    if (hoveredCellColumn) {
                                        if (index === hoveredCellColumn) columnModifier = 'topRowHighlighted';
                                        if (index === hoveredCellColumn - 1) columnModifier = 'columnPreceedingHighlighted';
                                        if (index === hoveredCellColumn + 1) columnModifier = 'columnFollowingHighlighted';
                                    }

                                    return (
                                        <th
                                            key={header + index}
                                            className={bem('tableHeaders', [columnModifier, selectedModifier])}
                                            onMouseOver={() => handleOnMouseOver(index)}
                                            onMouseOut={handleOnMouseOut}
                                        >
                                            <div className={bem('toggleContainer')}>
                                                {index === 0 && toggle && (
                                                    <ToggleSlide textLeft={textLeft()} textRight={textRight()} onToggle={onToggleUnits} />
                                                )}
                                            </div>
                                            <div
                                                className={bem('SkuAndRadioButton')}
                                                onClick={isSizeSelectable && index > 0 ? () => onSelectSkuHandler(header) : () => {}}
                                            >
                                                {isSizeSelectable && index > 0 && (
                                                    <span className={bem('radioButton', ['sameMargin', index === selectedColumnIndex && 'selected'], null)} />
                                                )}
                                                {header && <div>{header}</div>}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const rowsCount = rows.length;
                                const isBottomRow = index === rowsCount - 1;
                                const rowTypeModifier = isBottomRow ? 'bottomRowHighlighted' : 'columnHighlighted';

                                return (
                                    <tr key={index} data-count={rowsCount} className={bem('tableRows')}>
                                        {row.map((sizeValue, i) => {
                                            const hmamModifier =
                                                i === 0 &&
                                                coreMediaHMAM &&
                                                !loadingHMAM &&
                                                coreMediaHMAM?.[sizeValue] &&
                                                fetchMatchingHmamContentByMasterCategories(
                                                    sizeValue,
                                                    masterCategory,
                                                    coreMediaHMAM,
                                                    withOverlay,
                                                    coreMediaKey,
                                                ) &&
                                                'hmamInformationIcon';
                                            const borderFix = isBottomRow && i === 0 ? 'borderFix' : null;
                                            const cellKey = `${sizeValue}${index}${i}`;
                                            let selectedModifier = i === selectedColumnIndex - 1 && 'columnPreceedingHighlighted';
                                            selectedModifier = selectedModifier || (i === selectedColumnIndex && rowTypeModifier);
                                            selectedModifier = isSizeSelectable && selectedModifier;

                                            let columnModifier;

                                            if (hoveredCellColumn) {
                                                if (isBottomRow && i === hoveredCellColumn) columnModifier = 'bottomRowHighlighted';
                                                else {
                                                    if (i === hoveredCellColumn) columnModifier = 'columnHighlighted';
                                                    if (i === hoveredCellColumn - 1) columnModifier = 'columnPreceedingHighlighted';
                                                    if (i === hoveredCellColumn + 1) columnModifier = 'columnFollowingHighlighted';
                                                }
                                            }

                                            return (
                                                <td
                                                    key={cellKey}
                                                    className={bem('tableCells', [borderFix, columnModifier, selectedModifier])}
                                                    onMouseOver={() => handleOnMouseOver(i)}
                                                    onMouseOut={handleOnMouseOut}
                                                    onClick={hmamModifier ? setContent(sizeValue) : () => onSelectSkuHandler(headerRow[i])}
                                                >
                                                    <div data-testid={hmamModifier ? 'measuringGuide' : ''} className={bem(hmamModifier)}>
                                                        {
                                                            // TODO: remove "replace" after xtracker/pim issues have being fixed
                                                            sizeValue.replace('_', ' ')
                                                        }
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

SizeTable.propTypes = {
    sizeData: PropTypes.array(),
    masterCategory: PropTypes.array(),
    title: PropTypes.string,
    details: PropTypes.string,
    slideToScroll: PropTypes.string,
    sizeWrapperClass: PropTypes.string,
    toggle: PropTypes.bool,
    onToggleUnits: () => {},
    onSelectSku: () => {},
    skus: PropTypes.array,
    selectedSku: PropTypes.string,
    textLeft: PropTypes.string,
    textRight: PropTypes.string,
    setMeasuringGuide: PropTypes.func,
    setMeasurements: PropTypes.func,
    coreMediaHMAM: PropTypes.func,
    loadingHMAM: PropTypes.bool,
    features: PropTypes.object.isRequired,
    optimizely: PropTypes.object,
};

export default withOptimizely(SizeTable);
